/**
 * FAB — Floating Action Button primitive.
 *
 * Premium mobile pattern aligned with INDIGO v4 mockup (iris bg, anchored
 * above the bottom-nav, scroll-aware hide/show, haptic feedback). Uses
 * Portal so z-index ordering can't be broken by a parent's stacking
 * context.
 *
 * Reserved for one primary action per screen. If you need multiple
 * actions, use FABSpeedDial which wraps this with a long-press menu.
 *
 * Anchor math:
 *   - 56px bottom-nav height (Layout.tsx grid)
 *   - +env(safe-area-inset-bottom) iOS notch
 *   - +12px gap so FAB visually clears the nav divider
 *
 * Tap target: 56×56px (WCAG AAA Touch Target, larger than the mockup's
 * 50px — accessibility trumps pixel-perfect).
 */
import { useEffect, useRef, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useScrollDirection } from "./useScrollDirection";
import { useHaptic } from "./useHaptic";

export type FABProps = {
  /** Lucide-style icon component (size + strokeWidth props). */
  icon: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  /** Accessible label — required, even when icon-only. */
  label: string;
  /** Single-tap action. */
  onClick: () => void;
  /** Optional long-press action (~350ms hold). Fires once per gesture. */
  onLongPress?: () => void;
  /** Optional render-prop for a decorative children badge (used by FABSpeedDial for the speed-dial caret). */
  children?: ReactNode;
  /** Hide on scroll-down, reveal on scroll-up / idle. Default true. */
  scrollAware?: boolean;
  /** Tone — "primary" iris, "neutral" muted. Default primary. */
  tone?: "primary" | "neutral";
  /** Optional testId for E2E pins. Default "fab". */
  testId?: string;
};

const LONG_PRESS_MS = 350;

export function FAB({
  icon: Icon,
  label,
  onClick,
  onLongPress,
  children,
  scrollAware = true,
  tone = "primary",
  testId = "fab",
}: FABProps) {
  const haptic = useHaptic();
  const scrollDir = useScrollDirection();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const [pressed, setPressed] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Portal target: only mount client-side so SSR (none here, but safe) doesn't break.
  useEffect(() => {
    setMounted(true);
  }, []);

  const startPress = () => {
    setPressed(true);
    longPressFired.current = false;
    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        haptic.impact("medium");
        onLongPress();
      }, LONG_PRESS_MS);
    }
  };

  const endPress = () => {
    setPressed(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleClick = () => {
    // Suppress click if a long-press already fired (would double-trigger).
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    haptic.impact("light");
    onClick();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const hidden = scrollAware && scrollDir === "down";

  const fab = (
    <div
      className="fixed right-4 z-30 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 68px)" }}
    >
      <button
        type="button"
        aria-label={label}
        data-testid={testId}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPointerDown={startPress}
        onPointerUp={endPress}
        onPointerCancel={endPress}
        onPointerLeave={endPress}
        className={`pointer-events-auto relative inline-flex items-center justify-center w-14 h-14 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base transition-[transform,opacity,box-shadow] duration-300 will-change-transform ${
          tone === "primary"
            ? "bg-accent text-accent-on shadow-[0_8px_20px_rgb(var(--accent-rgb) / 0.42)]"
            : "bg-bg-raised text-fg-primary border border-bg-border shadow-[0_4px_14px_rgba(0,0,0,0.35)]"
        } ${pressed ? "scale-[0.92]" : "scale-100"} ${
          hidden ? "translate-y-[calc(100%+24px)] opacity-0" : "translate-y-0 opacity-100"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
      >
        <Icon size={24} strokeWidth={2.4} className="pointer-events-none" />
        {children}
      </button>
    </div>
  );

  if (!mounted) return null;
  return createPortal(fab, document.body);
}
